import { Link } from 'react-router-dom';
import { formatUnits } from 'viem';
import { Bank, Factory, Cube, Question, Heart, CheckCircle, Star } from '@phosphor-icons/react';
import { ProductDetails } from '../hooks/useEntityDetails';
import { useNavigate } from 'react-router-dom';
import { useProduct } from '../contexts/ProductContext';
import { useAccount } from 'wagmi';
import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useContractRead, useContractWrite } from 'wagmi';
import { CONFIG, ADDRESSES } from '../config';
import { Abi } from 'viem';
import { detectPngWithAlpha, getImageObjectFit, getImageBackground, getImagePadding } from '../utils/imageDetection';
import { useIpfsImage } from '../hooks/useIpfsImage';

// Map product types to icons
const PRODUCT_TYPE_ICONS: Record<string, typeof Bank> = {
  'TrinityBond': Bank,
  'TrinityFactory': Factory,
  'TrinityProduct': Cube,
  'Unknown': Question,
};

interface Asset {
  address: `0x${string}`;
  balance: bigint;
  symbol: string;
}

interface ProductCardProps {
  product: ProductDetails;
  entityName?: string;
  disableHoverScale?: boolean;
  entityAddress?: string;
  hideDetails?: boolean;
  hideDescription?: boolean;
}

export function ProductCard({ product, entityName, disableHoverScale = false, entityAddress, hideDetails = false, hideDescription = false }: ProductCardProps) {
  const navigate = useNavigate();
  const { setSelectedProduct } = useProduct();
  const { address } = useAccount();
  const [isFavorite, setIsFavorite] = useState(false);
  const [isHovered, setIsHovered] = useState(false);

  // Get entity verification status
  // Get the properties contract address from Trinity master
  const { data: trinityPropertiesAddress } = useContractRead({
    address: ADDRESSES.TRINITY_MASTER as `0x${string}`,
    abi: CONFIG.TRINITY_ABI.abi as Abi,
    functionName: 'properties',
    enabled: !!entityAddress,
  });

  // Get entity verification status from TrinityProperties contract
  const { data: verifiedProperty } = useContractRead({
    address: trinityPropertiesAddress as `0x${string}`,
    abi: CONFIG.TRINITY_PROPERTIES_ABI.abi as Abi,
    functionName: 'getProperty',
    args: [entityAddress as `0x${string}`, 'verified'],
    enabled: !!entityAddress && !!trinityPropertiesAddress,
  }) as { data: { boolValue: boolean } };

  const isVerified = verifiedProperty?.boolValue || false;

  // Get product properties contract address
  const { data: propertiesAddress } = useContractRead({
    address: product.address as `0x${string}`,
    abi: CONFIG.TRINITY_PRODUCT_ABI.abi as Abi,
    functionName: 'properties',
    enabled: !!product.address,
  }) as { data: `0x${string}` };

  // Get product average rating from TrinityProperties contract
  const { data: averageRating, error: ratingError, isLoading: ratingLoading } = useContractRead({
    address: propertiesAddress as `0x${string}`,
    abi: CONFIG.TRINITY_PROPERTIES_ABI.abi as Abi,
    functionName: 'getAverageRating',
    args: [],
    enabled: !!propertiesAddress && !!product.address,
  }) as { data: bigint; error: any; isLoading: boolean };

  // Get product rating count
  const { data: ratingCount } = useContractRead({
    address: propertiesAddress as `0x${string}`,
    abi: CONFIG.TRINITY_PROPERTIES_ABI.abi as Abi,
    functionName: 'getRatingCount',
    args: [],
    enabled: !!propertiesAddress && !!product.address,
  }) as { data: bigint };

  // Get product checkmarks (badges)
  const { data: badges } = useContractRead({
    address: propertiesAddress as `0x${string}`,
    abi: CONFIG.TRINITY_PROPERTIES_ABI.abi as Abi,
    functionName: 'getBadges',
    args: [product.address as `0x${string}`],
    enabled: !!propertiesAddress && !!product.address,
  }) as { data: Array<[string, string, bigint]> };

  const rating = averageRating ? Number(averageRating) : 0;
  const ratingCountNumber = ratingCount ? Number(ratingCount) : 0;

  // Calculate badge values and determine which badges to display
  const calculateBadgeValue = (badges: Array<[string, string, bigint]> | undefined) => {
    if (!badges || badges.length === 0) return 0;
    
    // Sum all badge timestamps as values (you can modify this logic based on your badge value system)
    const totalValue = badges.reduce((sum, badge) => sum + Number(badge[2]), 0);
    return totalValue / badges.length;
  };

  const badgeValue = calculateBadgeValue(badges);
  
  // Check if this is a CROWS asset and should display crow badge
  const shouldDisplayCrowBadge = () => {
    try {
      // Use the CROWS token address from config - fallback to hardcoded address if not in config
      const CROWS_TOKEN_ADDRESS = (ADDRESSES as any).CROWS || "0x5FC8d32690cc91D4c39d9d3abcBD16989F875707";
      if (!CROWS_TOKEN_ADDRESS || !product.address) return false;
      return product.address.toLowerCase() === CROWS_TOKEN_ADDRESS.toLowerCase() && badgeValue > 1;
    } catch (error) {
      console.warn('Error checking crow badge display:', error);
      return false;
    }
  };

  // Filter checkmark badges (keeping existing logic for backward compatibility)
  const checkmarkBadges = badges ? badges.filter(badge => badge[1] === 'checkmark') : [];
  const checkmarkCount = checkmarkBadges.length;

  // Debug logging for ratings
  console.log('=== PRODUCT CARD DEBUG ===');
  console.log('Product:', { name: product.name, address: product.address });
  console.log('Entity:', { name: entityName, address: entityAddress });
  console.log('Properties address:', propertiesAddress);
  console.log('Rating data:', { 
    averageRating: averageRating?.toString(), 
    ratingCount: ratingCount?.toString(),
    rating,
    ratingCountNumber,
    ratingError: ratingError?.message,
    ratingLoading
  });
  console.log('==========================');

  // Get product image from properties using getUIImage
  const { data: productImageUrl, error: imageError } = useContractRead({
    address: propertiesAddress as `0x${string}`,
    abi: CONFIG.TRINITY_PROPERTIES_ABI.abi as Abi,
    functionName: 'getUIImage',
    args: [0], // Get the first image (index 0)
    enabled: !!propertiesAddress && !!product.address,
  }) as { data: string; error: any };

  // Use the IPFS image hook for better gateway handling
  const { url: imageSrc, isLoading: imageLoading, error: ipfsError, hasLoadedOnce, isSuccessfullyLoaded } = useIpfsImage(productImageUrl, {
    maxRetries: 2,
    onError: (error) => {
      console.error('Failed to load product image:', error);
    },
    onSuccess: (url) => {
      console.log('Product image loaded successfully:', url);
    }
  });

  // Determine image styling based on file type
  const isPngWithAlpha = detectPngWithAlpha(productImageUrl || '');
  const imageObjectFit = getImageObjectFit(productImageUrl || '');
  const imageBackground = getImageBackground(productImageUrl || '');
  const imagePadding = getImagePadding(productImageUrl || '');

  // State to track if image should be shown
  const [showImage, setShowImage] = useState(true);
  const [imageLoadError, setImageLoadError] = useState(false);
  const [stableImageUrl, setStableImageUrl] = useState<string>('');

  // Reset image state when imageSrc changes
  useEffect(() => {
    if (imageSrc) {
      setShowImage(true);
      setImageLoadError(false);
    }
  }, [imageSrc]);

  // Only update stable image URL when we have a successful load
  useEffect(() => {
    if (isSuccessfullyLoaded && imageSrc) {
      setStableImageUrl(imageSrc);
    }
  }, [isSuccessfullyLoaded, imageSrc]);

  // Handle image load error
  const handleImageError = (e: React.SyntheticEvent<HTMLImageElement, Event>) => {
    console.error('Product image failed to load:', {
      src: imageSrc,
      productName: product.name,
      productAddress: product.address
    });
    setImageLoadError(true);
    setShowImage(false);
  };

  // Handle image load success
  const handleImageLoad = () => {
    console.log('Product image loaded successfully:', {
      productName: product.name,
      imageSrc: imageSrc?.substring(0, 100) + '...'
    });
    setImageLoadError(false);
    setShowImage(true);
  };

  // Debug logging for image loading
  useEffect(() => {
    console.log('ProductCard image state:', {
      productName: product.name,
      productImageUrl,
      imageSrc,
      stableImageUrl,
      showImage,
      imageLoadError,
      hasLoadedOnce,
      isSuccessfullyLoaded,
      isLoading: imageLoading
    });
  }, [product.name, productImageUrl, imageSrc, stableImageUrl, showImage, imageLoadError, hasLoadedOnce, isSuccessfullyLoaded, imageLoading]);


  // Load favorite status from localStorage
  useEffect(() => {
    if (address) {
      const favorites = JSON.parse(localStorage.getItem(`favorites_${address}`) || '[]');
      setIsFavorite(favorites.includes(product.address));
    }
  }, [address, product.address]);

  const handleClick = () => {
    navigate(`/product/${product.address}`);
  };

  const handleFavoriteClick = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent card click
    if (!address) return;

    const favorites = JSON.parse(localStorage.getItem(`favorites_${address}`) || '[]');
    let newFavorites: string[];

    if (isFavorite) {
      newFavorites = favorites.filter((addr: string) => addr !== product.address);
    } else {
      newFavorites = [...favorites, product.address];
    }

    localStorage.setItem(`favorites_${address}`, JSON.stringify(newFavorites));
    setIsFavorite(!isFavorite);
  };

  const ProductIcon = PRODUCT_TYPE_ICONS[product.className as keyof typeof PRODUCT_TYPE_ICONS] || PRODUCT_TYPE_ICONS.Unknown;

  // Render star rating with half-star support
  const renderStars = (rating: number) => {
    return Array.from({ length: 5 }, (_, index) => {
      const starPosition = index + 1;
      const isFilled = starPosition <= Math.floor(rating);
      const isHalfFilled = !isFilled && (starPosition - rating <= 0.5 && rating % 1 !== 0);
      
      return (
        <div key={index} className="relative inline-block">
          <Star
            size={12}
            weight="regular"
            className="text-gray-400"
          />
          {(isFilled || isHalfFilled) && (
            <div className="absolute inset-0 overflow-hidden">
              <Star
                size={12}
                weight="fill"
                className={`text-yellow-400 ${isHalfFilled ? 'clip-path-half' : ''}`}
                style={isHalfFilled ? { clipPath: 'inset(0 50% 0 0)' } : {}}
              />
            </div>
          )}
        </div>
      );
    });
  };

  return (
    <motion.div
      whileHover={disableHoverScale ? {} : { scale: 1.05, rotateY: 5 }}
      whileTap={disableHoverScale ? {} : { scale: 0.95 }}
      className={`${disableHoverScale ? 'w-full h-full' : 'aspect-square'} ${disableHoverScale ? 'rounded-2xl' : 'bg-white/10 backdrop-blur-sm rounded-lg'} overflow-hidden cursor-pointer transform perspective-1000 ${disableHoverScale ? '' : 'hover:bg-white/15'} relative`}
      onClick={handleClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Product Image Background - Full width/height */}
      {stableImageUrl && showImage && !imageLoadError && (
        <div className="absolute inset-0 w-full h-full">
          <img 
            src={stableImageUrl} 
            alt={product.name}
            className={`w-full h-full ${imageObjectFit} p-0 m-0`}
            onError={handleImageError}
            onLoad={handleImageLoad}
          />
          {/* Dark overlay for better text readability */}
          <div className="absolute inset-0 bg-black/40" />
        </div>
      )}

      {/* Fallback gradient background if no image or image failed to load */}
      {(!stableImageUrl || !showImage || imageLoadError) && (
        <div className="absolute inset-0 w-full h-full bg-gradient-to-br from-blue-500/20 to-purple-500/20" />
      )}

      {/* Favorite Heart Icon */}
      <div className="absolute top-2 right-2 z-10">
        <button
          onClick={handleFavoriteClick}
          className={`p-1 rounded-full transition-all duration-200 ${
            isHovered || isFavorite ? 'bg-white/20' : 'bg-black/20'
          }`}
        >
          <Heart
            size={16}
            weight={isFavorite ? 'fill' : 'regular'}
            className={`transition-all duration-200 ${
              isFavorite ? 'text-red-500' : isHovered ? 'text-red-400' : 'text-white'
            }`}
          />
        </button>
      </div>

      {/* Content anchored to bottom */}
      <div className="absolute bottom-0 left-0 right-0 p-3 bg-gradient-to-t from-black/80 via-black/40 to-transparent">
        <div className="flex items-start justify-center mb-1">
          <div className="flex items-center gap-2">
            <h3 className="text-md font-semibold text-white text-center leading-tight">{product.name}</h3>
            {shouldDisplayCrowBadge() && (
              <img 
                src="/images/crow_badge.jpg" 
                alt="Crow Badge" 
                className="w-4 h-4 rounded-full"
                onError={(e) => {
                  // Hide image if it fails to load
                  const target = e.target as HTMLImageElement;
                  target.style.display = 'none';
                }}
              />
            )}
          </div>
        </div>
        
        {entityName && !hideDetails && (
          <div className="flex items-center justify-center gap-1 mb-1">
            <p className="text-xs text-center text-white/80">by </p>
            <button
              onClick={(e) => {
                e.stopPropagation();
                if (entityAddress) {
                  navigate(`/entity/${entityAddress}`);
                }
              }}
              className="text-xs p-0 text-center bg-transparent text-blue-300 hover:text-blue-200 transition-colors underline"
            >
              {entityName}
            </button>
            {isVerified && (
              <CheckCircle size={12} className="text-blue-300" weight="fill" />
            )}
          </div>
        )}
        
        {!hideDescription && !hideDetails && (
          <p className="text-xs text-center text-white/80 line-clamp-2 mb-2">
            {product.description || 'No description available'}
          </p>
        )}
        
        {/* Star Rating */}
        <div className="flex justify-center items-center gap-1 mb-1">
          {renderStars(rating)}
          <span className="text-xs text-white/60">({ratingCountNumber})</span>
        </div>
        
        {/* Checkmark Display */}
        {checkmarkCount > 0 && (
          <div className="flex justify-center items-center gap-1 mb-1">
            <span className="text-xs text-green-300">✓ {checkmarkCount}</span>
          </div>
        )}
        
        {/* Wallet Connection Message */}
        {!address && (
          <div className="flex justify-center">
            <p className="text-xs text-white/60">
              Connect wallet to rate this product
            </p>
          </div>
        )}
      </div>
    </motion.div>
  );
} 